import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';

import { Plan } from '../../models/plan';
import { planColumns } from './grid-columns';
import { SiTrackPage } from '../../components/si-track-page';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { planApiKeyStore } from '../../api/plan-api-key-store';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { SelectChainItem, SelectConfig, SelectResult } from '../../components/select/select-chain.component';
import { planFormSchema } from './plan-form.schema';
import { UserPermissionName } from '../../models/user-permission-name';
import { GuardedComponent } from '../../components/guarded-component';
import { CreatePlanDialog } from './create-plan/create-plan.dialog';
import { useFileDownload } from '../../components/file-download';
import { MovePlanDialog } from './map-plan/move-plan.dialog';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { PlanStatusDialog } from './plan-status.dialog';
import { useAuthorize } from '../../hooks/use-authorize';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    requestedPage: params.get('requestedPage'),
  };
};

interface CellProps {
  planData: any;
}

interface CustomGridCellProps {
  field: string;
}

export const PlanLibraryPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [openDialog, setDialogAction] = useState<boolean>(false);
  const [planStatusValue, setPlanStatusValue] = useState<any>();
  const [selectedFilters, setSelectedFilter] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [editItem, setEditItem] = useState<Partial<Plan>>();

  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

  const handleFilterSelected = (filter: SelectResult) => {
    setSelectedFilter(filter);
  };

  const handleEditPlan = (plan: Plan) => {
    setEditItem(plan);
  };
  const handleEditClose = () => {
    setEditItem(undefined);
  };
  const handleCreatePlan = () => {
    setEditItem({ customerID: selectedFilters?.customerID, officeID: selectedFilters?.officeID });
  };

  const handleSubmitFilters = (filter: SelectResult) => {
    setSelectedFilter(filter);
    setEditItem(undefined);
  };

  const handleDownloadFile = async (data: any) => {
    const value = {
      planID: Number(data?.planID) || 0,
      fileName: data?.fileName,
      fileType: data?.fileType,
    };
    downloadFile({ additionalData: value });
  };

  const handleMovePlan = (data: any) => {
    setSelectedPlan(data);
    setDialogAction(true);
  };

  const selectConfig: SelectConfig[] = [
    {
      title: 'Customer',
      optionsLabelKey: 'customerName',
      optionsValueKey: 'customerID',
      type: 'autocomplete',
      getQueryKeyConfig: () => customersApiKeyStore.getCustomers(),
    },
    {
      title: 'Office',
      optionsLabelKey: 'officeName',
      optionsValueKey: 'officeID',
      type: 'autocomplete',
      disabled: !selectedFilters?.customerID,
      getQueryKeyConfig: (value?: SelectResult) => officeApiKeyStore.getCustomerOffices(value?.customerID),
    },
  ];

  const handleStatusChange = (planID: any, status: any) => {
    setPlanStatusValue({ planID: planID, isActive: status });
  };

  const FileDownloadCell = ({ planData }: CellProps) => {
    const fileURL = planData?.fileURL;

    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(planData)}>
              <Tooltip title={<Typography variant="body1">Download</Typography>}>
                <DownloadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <Typography variant="body1">No file uploaded</Typography>
        )}
      </td>
    );
  };

  const isEditPlanPermission = useAuthorize([UserPermissionName?.EDIT_PLAN]);

  const PlanStatusCell = ({ planData }: CellProps) => {
    const status = planData?.planStatus;
    const [isHovered, setIsHovered] = useState(false);
    const handleClickUnit = () => { handleStatusChange(planData?.planID, status) };

    return (
      <td>
        {isEditPlanPermission ?
          <Typography onClick={handleClickUnit}
            component="span"
            style={{
              color: isHovered ? '#0000b5' : '#0000b5',
              textDecoration: isHovered ? 'underline' : 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {status ? "Active" : "Inactive"}
          </Typography>
          :
          <Typography component="span">{status ? "Active" : "Inactive"}</Typography>
        }
      </td >
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'fileName') {
      return (dataItem: any) => {
        return <FileDownloadCell planData={dataItem.dataItem} />;
      };
    }
    if (field === 'planStatus') {
      return (dataItem: any) => {
        return <PlanStatusCell planData={dataItem.dataItem} />;
      };
    }
  };

  const mapAndDownloadElement = (dataItem: any) => {
    return (
      <>
        {/* <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_PLAN]}>
                    <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleMovePlan(dataItem?.dataItem)}>
                        <Tooltip title={<Typography variant="body1">Map</Typography>}>
                            <ArrowCircleRightOutlinedIcon style={{ color: '#E74C3C' }} />
                        </Tooltip>
                    </IconButton>
                </GuardedComponent> */}
      </>
    );
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('PlanLibrary', 'PlanLibrary'));

  const {
    isLoading,
    isFetching,
    data: PlanLibraries,
    refetch,
  } = useLoadData<ApiResponse<Plan[]>>(planApiKeyStore.getPlans({ ...selectedFilters }));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredPlanLibraries, setFilteredPlanLibraries] = useState<Plan[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredPlanLibraryList = PlanLibraries?.data?.filter((PlanLibrary) =>
      Object.values(PlanLibrary).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredPlanLibraries(filteredPlanLibraryList || []);
  }, [debouncedSearchTerm, PlanLibraries?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'PlanLibrary',
      pageName: 'PlanLibrary',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'PlanLibrary',
      pageName: 'PlanLibrary',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage
      title="Plan Library"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PLAN]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreatePlan}
          >
            Add Plan / File
          </Button>
        </GuardedComponent>
      }
    >
      <div className="innerContent">
        <div className="mBm">
          <SelectChainItem config={selectConfig} value={selectedFilters} onSelect={handleFilterSelected} />
        </div>
        <Paper elevation={0} style={styling.paperStyle}>
          <SiTrackGrid
            fileTitle='plan_library'
            columns={planColumns}
            dataItemKey="planID"
            // queryConfig={planApiKeyStore.getPlans({ ...selectedFilters })}
            customCell={(field) =>
              CustomGridCell({
                field,
              })
            }
            onEdit={handleEditPlan}
            // CustomComponent={mapAndDownloadElement}
            deleteMutationConfig={(item) => planApiKeyStore.deletePlan(item.planID)}
            actionPermissions={{
              edit: UserPermissionName.EDIT_PLAN,
              delete: UserPermissionName.DELETE_PLAN,
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            isBusy={isLoading || isFetching || isFileDownloadLoading || isLoadingGridLayout}
            // -----------------Grid Layout-------
            dataItems={filteredPlanLibraries || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
            reFixPageNumber
          // -----------------Grid Layout-------
          />
        </Paper>
      </div>
      {editItem && (
        <CreatePlanDialog
          title={editItem.planID ? 'UPDATE PLAN / FILE' : 'CREATE PLAN / FILE'}
          data={editItem}
          config={{
            saveConfig: editItem?.planID ? planApiKeyStore.updatePlan() : planApiKeyStore.createPlan(),
            invalidateRoute: () => planApiKeyStore.getPlans({ ...selectedFilters }),
          }}
          yupSchema={planFormSchema}
          onSubmitFilters={handleSubmitFilters}
          onClosed={handleEditClose}
        />
      )}
      {openDialog && <MovePlanDialog planData={selectedPlan} onClosed={() => setDialogAction(false)} />}
      {planStatusValue && (
        <PlanStatusDialog
          statusValue={planStatusValue}
          isPlanLoading={isLoading || isFetching}
          refetching={refetch}
          onClosed={() => setPlanStatusValue(undefined)} />
      )}
    </SiTrackPage>
  );
};
